import {
  ArcoCreateOrUpdateDto,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useGetNodoPortale,
  useUpdateTrasferimenti,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import { portaleConfig } from "@/portaliConfig";
import { getDispositivoTypeLabel } from "@/utils/dispositivoUtils";
import {
  IS_NEW_KEY,
  useDbRelationsUpdateForm,
} from "@/utils/useDbRelationsUpdate";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { DateTimeField } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { withOpeningReset } from "../../utils/withOpeningReset";
import GatewayPicker from "../GatewayPicker";
import { SmallLoading } from "../Loading";

const columnHelper = createColumnHelper<ArcoCreateOrUpdateDto>();
function ConnessioniGatewayDialog({
  open = false,
  canEdit = false,
  onClose,
  nodo: _nodo,
}: {
  open?: boolean;
  canEdit?: boolean;
  onClose?: () => void;
  nodo?: NodoDto;
}) {
  const { t } = useTranslation();
  const { data: fullNodoData } = useGetNodoPortale(
    portaleConfig.id,
    _nodo?.id ?? 0,
    {
      query: {
        enabled: !!_nodo,
      },
    }
  );

  const nodo = fullNodoData?.data;

  const dispositivoLabel = useMemo(() => {
    if (
      nodo?.tipo === "Dispositivo_Macchinario" ||
      nodo?.tipo === "Dispositivo_Sensore"
    ) {
      return `${getDispositivoTypeLabel(
        nodo?.dispositivoModello!.tipoDispositivo!
      )} ${nodo?.dispositivoMatricola}`;
    } else if (nodo?.tipo === "Ripetitore") {
      return t("connessioniGatewayDialog.ripetitore", { nome: nodo.nome });
    } else {
      return "";
    }
  }, [nodo, t]);

  const handleClose = () => {
    onClose?.();
  };

  const editing = canEdit;

  const initialConnessioniGateway = useMemo(() => {
    return nodo?.connessioniGateway ?? [];
  }, [nodo?.trasferimentiBreadcrumbs]);

  const {
    data,
    control,
    resetField,
    watch,
    handleSubmit,
    getChangeSubmitData,
    create,
    remove,
    restore,
    getFieldName,
    setValue,
    getEntityState,
  } = useDbRelationsUpdateForm<ArcoCreateOrUpdateDto>(
    initialConnessioniGateway,
    {
      deleteChangeType: "deleted",
    }
  );

  const { hasPermission } = useAuth();

  const columns = useMemo<ColumnDef<ArcoCreateOrUpdateDto, any>[]>(() => {
    const editingDataTimeCell = (
      _: string | null | undefined,
      name: ReturnType<typeof getFieldName>
    ) => {
      return (
        <Controller
          name={name as any}
          control={control}
          render={({ field }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <DateTimeField
                ref={ref}
                format="DD/MM/YYYY HH:mm"
                name={name}
                onBlur={onBlur}
                onChange={(value) => onChange(value?.toISOString())}
                value={m}
              />
            );
          }}
        />
      );
    };

    const readonlyDataTimeCell = (value: string | null | undefined) => {
      const m = value ? moment(value) : null;
      return m ? m.format("L LT") : "";
    };

    const dataTimeCell = editing ? editingDataTimeCell : readonlyDataTimeCell;

    const columns = [
      columnHelper.accessor("da", {
        header: t("date.da"),
        size: 100,
        maxSize: 100,
        cell: ({ getValue, row }) =>
          dataTimeCell(getValue(), getFieldName(row.index, "da")),
      }),
      columnHelper.accessor("a", {
        header: t("date.a"),
        size: 100,
        maxSize: 100,
        cell: ({ getValue, row }) =>
          dataTimeCell(getValue(), getFieldName(row.index, "a")),
      }),
      columnHelper.display({
        header: t("connessioniGatewayDialog.colonne.gateway"),
        cell: ({ row }) => {
          return (
            <Controller
              control={control}
              name={getFieldName(row.index, "sorgenteId")}
              render={({ field }) => {
                return (
                  <GatewayPicker
                    value={field.value ?? null}
                    onChange={(gatewayId, aziendaId) => {
                      console.log({ gatewayId, aziendaId });
                      field.onChange(gatewayId ?? undefined);
                      setValue(
                        getFieldName(row.index, "sorgenteAziendaId"),
                        aziendaId ?? undefined
                      );
                    }}
                    disabled={!editing}
                  />
                );
              }}
            />
          );
        },
      }),
      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 30,
          cell: ({ row }) => {
            return (
              <>
                <Box display="flex" gap={1}>
                  <IconButton
                    title={t("azioni.elimina")}
                    size="small"
                    color="error"
                    onClick={() => {
                      remove(row.index);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box
                  display="flex"
                  gap={1}
                  className="deleted-row-visible-content"
                >
                  <IconButton
                    title={t("azioni.ripristina")}
                    size="small"
                    onClick={() => {
                      restore(row.index);
                    }}
                  >
                    <RestoreFromTrashIcon />
                  </IconButton>
                </Box>
              </>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [
    editing,
    control,
    getFieldName,
    hasPermission,
    resetField,
    watch,
    remove,
    restore,
    t,
  ]);

  const isRowDeleted = useCallback(
    (row: Row<ArcoCreateOrUpdateDto>) => {
      return getEntityState(row.index) === "deleted";
    },
    [getEntityState]
  );

  const { mutateAsync, isLoading, error } = useUpdateTrasferimenti({
    mutation: {
      onSuccess: () => {
        toast.success(t("connessioniGatewayDialog.toast.salvate"));
      },
      onError: () => {
        toast.error(t("errori.richiesta"));
      },
    },
  });

  const queryClient = useQueryClient();

  const onSubmit = async () => {
    if (!nodo) {
      return;
    }

    const data = getChangeSubmitData();
    console.log("change data:", data);
    try {
      await mutateAsync({
        id: nodo?.id ?? 0,
        data: data.map((change) => {
          if (change.changeType === "added") {
            change.entity.destinazioneId = nodo.id;
          }
          return change;
        }),
      });

      queryClient.invalidateQueries({
        queryKey: getGetNodoQueryKey(nodo!.id),
      });
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo!.id),
      });
      onClose?.();
    } catch (err) {}
  };

  const isValid = useMemo(() => {
    const { data } = watch();
    return data.every((x: ArcoCreateOrUpdateDto) => {
      return x.sorgenteId;
    });
  }, [watch()]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={false}>
      <DialogTitle>
        {t("connessioniGatewayDialog.titolo", {
          dispositivo: dispositivoLabel,
        })}
      </DialogTitle>
      <DialogContent>
        <Box>
          <Box>
            {editing && (
              <Button
                size="small"
                color="light"
                onClick={() => {
                  create();
                }}
              >
                {t("connessioniGatewayDialog.azioni.aggiungi")}
              </Button>
            )}
          </Box>
          <Table
            columns={columns}
            data={data}
            sortable={false}
            initialSort={[
              { id: IS_NEW_KEY, desc: false },
              { id: "da", desc: true },
            ]}
            isRowDeleted={isRowDeleted}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        {editing && (
          <Button
            size="small"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid}
          >
            {t("azioni.salva")}
          </Button>
        )}
        {onClose && (
          <Button size="small" color="light" onClick={handleClose}>
            {t("azioni.chiudi")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(ConnessioniGatewayDialog);
